<!-- src/components/UserProfileModal.vue -->
<template>
  <div
    id="userProfileModal"
    class="modal core-materialize-modal core-materialize-modal--small"
  >
    <div class="modal-content">
      <h5 class="core-section-title">User Profile</h5>

      <div class="row" style="margin-top: 14px">
        <div class="col s12 center-align">
          <img
            :src="previewPhoto || form.photoUrl || fallbackPhoto"
            class="circle"
            style="width: 96px; height: 96px; object-fit: cover"
            alt="User avatar"
          />

          <div
            class="file-field input-field"
            style="max-width: 320px; margin: 16px auto 0"
          >
            <div class="btn blue-grey darken-1">
              <span>Choose Image</span>

              <input type="file" accept="image/*" @change="onPhotoSelected" />
            </div>

            <div class="file-path-wrapper">
              <input
                class="file-path validate"
                type="text"
                placeholder="Upload a profile image"
              />
            </div>
          </div>

          <div class="core-helper" style="margin-top: 6px">JPG or PNG · Max 5MB</div>
        </div>
      </div>

      <div class="row" style="margin-top: 10px">
        <div class="input-field col s12 m6">
          <input id="profile_first_name" v-model="form.firstName" type="text" />

          <label class="active" for="profile_first_name"> First Name </label>
        </div>

        <div class="input-field col s12 m6">
          <input id="profile_last_name" v-model="form.lastName" type="text" />

          <label class="active" for="profile_last_name"> Last Name </label>
        </div>
      </div>

      <div v-if="error" class="core-feedback core-feedback--error">
        {{ error }}
      </div>
    </div>

    <div class="modal-footer">
      <a href="#!" class="modal-close btn-flat" :class="{ disabled: saving }"> Cancel </a>

      <a href="#!" class="btn" :class="{ disabled: saving }" @click.prevent="save">
        Save
      </a>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";

import { auth, db, storage } from "@/firebase";

import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

import { ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";

const form = ref({
  firstName: "",
  lastName: "",
  photoUrl: "",
});

const previewPhoto = ref("");
const selectedFile = ref(null);
const saving = ref(false);
const error = ref("");

const fallbackPhoto = computed(() => auth.currentUser?.photoURL || "");

async function load() {
  const user = auth.currentUser;

  if (!user) {
    return;
  }

  const snap = await getDoc(doc(db, "users", user.uid));

  const data = snap.exists() ? snap.data() : {};

  const googleParts = (user.displayName || "").split(" ");

  const googleFirst = googleParts[0] || "";

  const googleLast = googleParts.slice(1).join(" ");

  form.value = {
    firstName: data.firstName ?? googleFirst,

    lastName: data.lastName ?? googleLast,

    photoUrl: data.photoUrl ?? user.photoURL ?? "",
  };
}

function onPhotoSelected(e) {
  const file = e?.target?.files?.[0];

  if (!file) {
    return;
  }

  selectedFile.value = file;

  previewPhoto.value = URL.createObjectURL(file);
}

async function save() {
  error.value = "";

  const user = auth.currentUser;

  if (!user) {
    return;
  }

  saving.value = true;

  try {
    let photoUrl = form.value.photoUrl || user.photoURL || "";

    if (selectedFile.value) {
      const ext = selectedFile.value.name.split(".").pop()?.toLowerCase() || "jpg";

      const path = `user-avatars/${user.uid}.${ext}`;

      const avatarRef = sRef(storage, path);

      await uploadBytes(avatarRef, selectedFile.value);

      photoUrl = await getDownloadURL(avatarRef);
    }

    await setDoc(
      doc(db, "users", user.uid),

      {
        firstName: form.value.firstName || "",

        lastName: form.value.lastName || "",

        photoUrl: photoUrl || "",

        updatedAt: serverTimestamp(),
      },

      {
        merge: true,
      }
    );

    selectedFile.value = null;

    previewPhoto.value = "";

    const el = document.getElementById("userProfileModal");

    const inst = window.M?.Modal?.getInstance(el);

    inst?.close();
  } catch (e) {
    console.error("[UserProfileModal] Save failed:", e);

    error.value = "Failed to save profile. Please try again.";
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  const el = document.getElementById("userProfileModal");

  if (el && window.M?.Modal) {
    window.M.Modal.init(el, {
      dismissible: true,
      opacity: 0.5,
      inDuration: 150,
      outDuration: 120,
    });
  }

  await load();
});

defineExpose({
  load,
});
</script>
